import React, { useState, useCallback, useEffect } from 'react';
import { View, StyleSheet, Platform, ViewStyle } from 'react-native';
import { useSafeAreaInsets, initialWindowMetrics } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '@/navigation/RootNavigator';
import { UnifiedModal, ModalButton } from '@/components/common/UnifiedModal';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useMaintenanceForm } from '@/hooks/residents/useMaintenanceForm';
import { useMaintenanceSubmitFlow } from '@/hooks/residents/useMaintenanceSubmitFlow';
import { MaintenanceSubmitStatus } from '@/components/residents/maintenance/shared';
import { useAppSelector } from '@/store';
import { showConfirm, showAlert } from '@/utils/common/alert';
import { useThemedStyles } from '@/hooks/residents/useThemeColors';
import { MaintenanceStep } from '@/utils/residents/maintenance/maintenanceValidation';

import { StepIssue } from '@/components/residents/maintenance/mobile/steps/StepIssue';
import { StepDetailsRedesign } from '@/components/residents/maintenance/mobile/steps/StepDetailsRedesign';
import { StepUrgency } from '@/components/residents/maintenance/mobile/steps/StepUrgency';
import { StepAccess } from '@/components/residents/maintenance/mobile/steps/StepAccess';

const STEPS: MaintenanceStep[] = ['issue', 'details', 'urgency', 'access'];
const TOTAL_STEPS = STEPS.length;

const STEP_ALERT_TITLE: Record<MaintenanceStep, string> = {
  issue: 'Selection Required',
  details: 'Title Required',
  urgency: 'Selection Required',
  access: 'Time Slots Required',
};

export const MobileCreateMaintenanceScreen: React.FC = () => {
  const styles = useCreateMaintenanceStyles();
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const user = useAppSelector((s) => s.auth.user as any);
  const insets = useSafeAreaInsets();
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  const safeTop = Platform.OS === 'android'
    ? Math.max(insets.top, initialWindowMetrics?.insets?.top ?? 0)
    : insets.top;

  const {
    description, setDescription,
    location,
    category,
    subCategory, setSubCategory,
    problem,
    severity, setSeverity,
    phoneNumber,
    permissionChoice, setPermissionChoice,
    requestType, setRequestType,
    title, setTitle,
    hasPets, setHasPets,
    specialInstructions, setSpecialInstructions,
    timeSlots, setTimeSlots,
    validate,
    maintenanceInfo,
    subCategoryOptions,
    loadInfo,
    isInfoError,
    infoErrorStatus,
    mediaItems, setMediaItems,
  } = useMaintenanceForm();

  const { phase, submit } = useMaintenanceSubmitFlow();

  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  useEffect(() => {
    loadInfo();
  }, [loadInfo]);

  useEffect(() => {
    if (isInfoError && (infoErrorStatus === 500 || infoErrorStatus == null)) {
      console.error('Unable to load maintenance options');
    }
  }, [isInfoError, infoErrorStatus]);

  const currentStep = STEPS[currentStepIndex];
  const isLastStep = currentStep === 'access';

  const handleClose = useCallback(async () => {
    const hasData =
      title.trim() !== '' ||
      description.trim() !== '' ||
      mediaItems.length > 0 ||
      category !== '' ||
      severity !== '';

    if (hasData) {
      const confirmed = await showConfirm(
        'Discard Request?',
        'Are you sure you want to discard your work request?',
        'Discard',
        'Cancel'
      );
      if (confirmed) {
        if (navigation.canGoBack()) navigation.goBack();
      }
    } else {
      if (navigation.canGoBack()) navigation.goBack();
    }
  }, [title, description, mediaItems, category, severity, navigation]);

  const validateCurrentStep = useCallback((): boolean => {
    const { valid, message } = validate(currentStep);
    if (!valid) {
      showAlert(STEP_ALERT_TITLE[currentStep] || 'Required', message || 'Please complete this step.');
      return false;
    }
    return true;
  }, [currentStep, validate]);

  const isNextEnabled = useCallback((): boolean => validate(currentStep).valid, [validate, currentStep]);

  const handleBack = useCallback(() => {
    setCurrentStepIndex(prev => Math.max(prev - 1, 0));
  }, []);

  const handleSubmit = useCallback(async () => {
    await submit({
      requestType,
      category,
      subCategory,
      maintenanceInfo,
      location,
      problem,
      severity,
      title,
      description,
      phoneNumber,
      permissionChoice,
      attachments: mediaItems.map(m => ({
        uri: m.uri,
        type: m.type,
        name: m.name,
        file: m.file as any,
      })),
      hasPets: hasPets ?? undefined,
      specialInstructions,
    });
  }, [
    requestType, category, subCategory, maintenanceInfo, location, problem,
    severity, title, description, phoneNumber, permissionChoice, mediaItems,
    hasPets, specialInstructions, submit,
  ]);

  const handleDone = useCallback(() => {
    if (navigation.canGoBack()) navigation.goBack();
  }, [navigation]);

  const handleNext = useCallback(() => {
    if (!validateCurrentStep()) return;

    if (isLastStep) {
      handleSubmit();
    } else {
      setCurrentStepIndex(prev => Math.min(prev + 1, TOTAL_STEPS - 1));
    }
  }, [validateCurrentStep, isLastStep, handleSubmit]);

  const userName = user?.firstName || 'there';
  const maintenancePhone = user?.property?.phone || user?.property?.twilioNumber;

  const isFirstStep = currentStepIndex === 0;
  const progressPercent = currentStepIndex === 0 ? 0 : (currentStepIndex / (TOTAL_STEPS - 1)) * 100;
  const nextEnabled = isNextEnabled();

  const renderStepContent = () => {
    switch (currentStep) {
      case 'issue':
        return (
          <StepIssue
            userName={userName}
            requestType={requestType}
            onRequestTypeChange={setRequestType}
            subCategory={subCategory}
            onSubCategoryChange={setSubCategory}
            subCategoryOptions={subCategoryOptions}
          />
        );
      case 'details':
        return (
          <StepDetailsRedesign
            title={title}
            onTitleChange={setTitle}
            description={description}
            onDescriptionChange={setDescription}
            mediaItems={mediaItems}
            onMediaItemsChange={setMediaItems}
          />
        );
      case 'urgency':
        return (
          <StepUrgency
            severity={severity}
            onSeverityChange={setSeverity}
            priorities={maintenanceInfo?.priorities ?? []}
            maintenancePhone={maintenancePhone}
          />
        );
      case 'access':
        return (
          <StepAccess
            permissionChoice={permissionChoice}
            onPermissionChoiceChange={setPermissionChoice}
            timeSlots={timeSlots}
            onTimeSlotsChange={setTimeSlots}
            hasPets={hasPets ?? false}
            onHasPetsChange={setHasPets}
            specialInstructions={specialInstructions}
            onSpecialInstructionsChange={setSpecialInstructions}
          />
        );
      default:
        return null;
    }
  };

  const safeBottom = Math.max(insets.bottom, initialWindowMetrics?.insets?.bottom ?? 0);
  const bottomPadding = Platform.OS === 'android'
    ? Math.max(safeBottom + 16, 32)
    : Math.max(safeBottom + 30, 40);

  const footerContent = (
    <View style={[styles.footerContainer, { paddingBottom: bottomPadding }]}>
      <View style={styles.footerRow}>
        <View style={[styles.footerButtonWrapper, isFirstStep && styles.buttonHidden]}>
          <ModalButton
            label="Back"
            variant="secondary"
            onPress={handleBack}
            disabled={isFirstStep}
          />
        </View>
        <View style={styles.footerButtonWrapper}>
          <ModalButton
            label={isLastStep ? 'Submit' : 'Next'}
            variant="primary"
            onPress={handleNext}
            disabled={!nextEnabled}
            animateOnPress={false}
          />
        </View>
      </View>
    </View>
  );

  return (
    <View style={[styles.safeArea, Platform.OS === 'ios' && { paddingTop: safeTop }]}>
      <UnifiedModal
        visible={true}
        onClose={phase === 'form' ? handleClose : phase === 'success' ? handleDone : noop}
        variant={isDesktop ? 'dialog' : 'fullscreen'}
        dialogSize="large"
        animateEntry={false}
        scrollable
        avoidKeyboard
        compensateKeyboardToolbar
        dismissOnBackdrop={false}
        footer={phase === 'form' ? footerContent : undefined}
        footerStyle={footerPaddingReset}
        contentContainerStyle={scrollContentOverride}
      >
        {phase === 'form' ? (
          <>
            <View style={styles.progressBarBackground}>
              <View style={[styles.progressBarFill, { width: `${progressPercent}%` }]} />
            </View>
            {renderStepContent()}
          </>
        ) : (
          <MaintenanceSubmitStatus
            phase={phase}
            hasAttachments={mediaItems.length > 0}
            onDone={handleDone}
          />
        )}
      </UnifiedModal>
    </View>
  );
};

const useCreateMaintenanceStyles = () =>
  useThemedStyles(({ colors, semantic }) =>
    StyleSheet.create({
      safeArea: {
        flex: 1,
        backgroundColor: colors.bg.modal,
      },
      progressBarBackground: {
        height: 8,
        backgroundColor: colors.glass.buttonBg,
        borderRadius: 8,
        overflow: 'hidden',
        marginBottom: 16,
      },
      progressBarFill: {
        height: '100%',
        backgroundColor: semantic.interactive.primary,
        borderRadius: 8,
      },
      footerContainer: {
        paddingHorizontal: 24,
        paddingTop: 16,
        borderTopWidth: 1,
        borderTopColor: colors.separator,
      },
      footerRow: {
        flexDirection: 'row',
        gap: 12,
      },
      footerButtonWrapper: {
        flex: 1,
      },
      buttonHidden: {
        opacity: 0,
      },
    }),
  );

const noop = () => {};
const footerPaddingReset: ViewStyle = { paddingHorizontal: 0, paddingTop: 0, paddingBottom: 0, borderTopWidth: 0 };
const scrollContentOverride: ViewStyle = { paddingHorizontal: 0, paddingBottom: 24, flexGrow: 1 };

export default MobileCreateMaintenanceScreen;
